import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { partnerApi, type PartnerUser } from '../../lib/api';
import { contactLabel } from './tool-test-contacts';
import styles from './ToolTestPanel.module.css';

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LENGTH_HINT = `Enter at least ${MIN_SEARCH_LENGTH} characters to search.`;
/** Mirrors the columns the partner-users search ORs over — phone is not one of them. */
const SEARCH_PLACEHOLDER = 'Name, email, or external ID';

interface ToolTestContactPickerProps {
  businessId: string;
  searchLabel: string;
  selectedContact: PartnerUser | null;
  onSelect: (contact: PartnerUser | null) => void;
  hint?: ReactNode;
}

/**
 * Picks the contact a test run acts as, searched by the partner's own identifiers. Reach's
 * `partner_users.id` is what the run submits, but partners never see or type it — in production
 * it arrives as a token claim, so there is no UUID for them to know.
 */
export const ToolTestContactPicker = ({
  businessId,
  searchLabel,
  selectedContact,
  onSelect,
  hint,
}: ToolTestContactPickerProps) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const trimmedSearch = search.trim();
  // Debouncing is a timer, so there is no render-time equivalent to derive it from.
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(trimmedSearch), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [trimmedSearch]);

  const contactsQuery = useQuery({
    queryKey: ['partner-users', { businessId, search: debouncedSearch, status: 'active' }],
    queryFn: () =>
      partnerApi.getPartnerUsers({
        tenantId: businessId,
        search: debouncedSearch,
        status: 'active',
        limit: 20,
      }),
    enabled: Boolean(businessId) && debouncedSearch.length >= MIN_SEARCH_LENGTH,
  });
  const contacts = (contactsQuery.data?.data.results ?? []).filter(
    contact => contact.status === 'active' && !contact.deactivatedAt,
  );

  const searchTooShort = Boolean(businessId) && trimmedSearch.length < MIN_SEARCH_LENGTH;
  const resultsSettled = contactsQuery.isSuccess && debouncedSearch === trimmedSearch;
  const selectionRetired =
    resultsSettled &&
    Boolean(selectedContact) &&
    !contacts.some(contact => contact.id === selectedContact?.id);

  // A background refetch can deactivate the picked contact out of the list; the run submits from
  // the parent's state, so dropping it here is what stops a retired contact reaching the server.
  useEffect(() => {
    if (selectionRetired) onSelect(null);
  }, [selectionRetired, onSelect]);

  return (
    <>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>{searchLabel}</span>
        <input
          type="search"
          value={search}
          onChange={event => {
            setSearch(event.target.value);
            onSelect(null);
          }}
          disabled={!businessId}
          className={styles.input}
          placeholder={SEARCH_PLACEHOLDER}
        />
        {searchTooShort && <span className={styles.neutralHint}>{SEARCH_LENGTH_HINT}</span>}
        {hint}
      </label>

      {contactsQuery.isFetching && <span className={styles.neutralHint}>Searching contacts…</span>}
      {resultsSettled && (
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Identified contact</span>
          <select
            value={selectedContact?.id ?? ''}
            onChange={event =>
              onSelect(contacts.find(contact => contact.id === event.target.value) ?? null)
            }
            className={styles.selectFull}>
            <option value="">Choose a contact…</option>
            {contacts.map(contact => (
              <option key={contact.id} value={contact.id}>
                {contactLabel(contact)}
              </option>
            ))}
          </select>
          {contacts.length === 0 && (
            <span className={styles.neutralHint}>No active contacts matched this search.</span>
          )}
        </label>
      )}
    </>
  );
};
